import { Switch } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ClientDTO, configureClient, VmsAccount, VmsClient } from 'wisestep-api';
import { WsButton, WsSelect, WsTypoColors, WsTypography } from 'wisestep-base';

import { ClientAutocomplete } from '../../../../../components/ClientAutocomplete';
import RecruiterSuggestions from '../../../../../components/filters/components/RecruiterSuggestions';
import WsTeamFilter from '../../../../../components/filters/components/WsTeamsFilter';
import { configureVmsClientPayload } from '../../helper/payload';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  width: '32rem',
};

const gridStyle = {
  m: 1,
};

export interface IAddEmailModal {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  selectedVmsAccount: VmsAccount | undefined;
}

interface FormData {
  client: ClientDTO;
  team: string;
  jobOwner: string;
  fetchOlderJobs: boolean;
  fetchJobsSince: number;
}

const AddEmailDialog = ({ open, handleClose, onSuccess, selectedVmsAccount }: IAddEmailModal) => {
  const methods = useForm<FormData>({ defaultValues: { client: null } as unknown as FormData });
  const [fetchOlderJobsFlag, setFetchOlderJobsFlag] = useState<boolean>(false);
  const [showPayRates, setShowPayRates] = useState<boolean>(false);

  const fetchJobOptions = [
    {
      label: '30 Days',
      value: 30,
    },
    {
      label: '45 Days',
      value: 45,
    },
    {
      label: '60 Days',
      value: 60,
    },
    {
      label: '90 Days',
      value: 90,
    },
  ];

  const handleVmsCreation = async (data: FormData) => {
    if (
      selectedVmsAccount?.accountName != undefined &&
      selectedVmsAccount.subscription != undefined
    ) {
      const clientDto: VmsClient = {
        ...configureVmsClientPayload,
        id: '',
        accountId: selectedVmsAccount.id,
        jobOwner: JSON.stringify(data.jobOwner),
        team: JSON.stringify(data.team[0]),
        clientName: data.client.name,
        fetchOlderJobs: fetchOlderJobsFlag,
        fetchJobsSince: data.fetchJobsSince,
        showPayRates: showPayRates,
      };
      await configureClient([clientDto]);
      onSuccess();
      handleClose();
      methods.reset();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <FormProvider {...methods}>
        <Box sx={style} component={'form'} onSubmit={methods.handleSubmit(handleVmsCreation)}>
          <Grid container spacing={1}>
            <Grid
              container
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={gridStyle}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2" color="#00B4D8">
                Add Client
              </Typography>
            </Grid>
            <Grid
              item
              xs={4}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <WsTypography color={WsTypoColors.SECONDARY}>Client Name:</WsTypography>
            </Grid>
            <Grid item xs={8} justifyContent="center" alignItems="center" pl={2} pr={2}>
              <ClientAutocomplete
                name="client"
                placeHolder="Client Name"
                creatable={true}
                required={true}
              />
            </Grid>
            <Grid
              item
              xs={4}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <WsTypography color={WsTypoColors.SECONDARY}>Team:</WsTypography>
            </Grid>
            <Grid item xs={8} justifyContent="center" alignItems="center" pl={2} pr={2}>
              <WsTeamFilter
                name="team"
                placeHolder="Search Team"
                focused={true}
                multiple={false}
                requireTeam={false}
              />
            </Grid>
            <Grid
              item
              xs={4}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <WsTypography color={WsTypoColors.SECONDARY}>Job Owner:</WsTypography>
            </Grid>
            <Grid item xs={8} justifyContent="center" alignItems="center" pl={2} pr={2}>
              <RecruiterSuggestions
                name="jobOwner"
                placeholder={'Search Owner'}
                focused={true}
                multiple={false}
                requireRecruiter={true}
              />
            </Grid>
            <Grid
              item
              xs={4}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <WsTypography color={WsTypoColors.SECONDARY}>Fetch Older Jobs:</WsTypography>
            </Grid>
            <Grid item xs={8} justifyContent="left" alignItems="center" pl={2} pr={2} container>
              <Grid item container xs={5} alignItems="center" justifyContent="space-around">
                <WsTypography>No</WsTypography>
                <Switch
                  name="fetchOlderJobs"
                  checked={fetchOlderJobsFlag}
                  onChange={() => setFetchOlderJobsFlag(!fetchOlderJobsFlag)}
                />
                <WsTypography>Yes</WsTypography>
              </Grid>
              {fetchOlderJobsFlag && (
                <Grid item xs={7}>
                  <WsSelect
                    name="fetchJobsSince"
                    selectArray={fetchJobOptions}
                    placeholder="Select Days"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              )}
            </Grid>
            <Grid
              item
              xs={4}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <WsTypography color={WsTypoColors.SECONDARY}>Show Pay Rates:</WsTypography>
            </Grid>
            <Grid item xs={8} justifyContent="left" alignItems="center" pl={2} pr={2} container>
              <Grid item container xs={5} alignItems="center" justifyContent="space-around">
                <WsTypography>No</WsTypography>
                <Switch
                  name="showPayRates"
                  checked={showPayRates}
                  onChange={() => setShowPayRates(!showPayRates)}
                />
                <WsTypography>Yes</WsTypography>
              </Grid>
            </Grid>
          </Grid>
          <Grid mt={2} container direction="column" alignItems="center" justifyContent="center">
            <Grid item xs={12}>
              <WsButton
                variant="outlined"
                size="medium"
                onClick={() => {
                  handleClose();
                  methods.reset();
                }}
              >
                Skip
              </WsButton>
              &nbsp;
              <WsButton variant="contained" size="medium" type="submit">
                Save
              </WsButton>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </Modal>
  );
};
export default AddEmailDialog;
